import { PALETTE } from '../assets/palette';
import { mulberry32 } from '../core/rng';
import { clamp, lerp } from '../core/vec';
import type { Engine } from '../game/engine';
import { particleAlpha } from '../game/particles';
import type { Camera } from './camera';
import {
  drawBoomerangs, drawBolts, drawCoins, drawDamageNumbers, drawEnemies,
  drawFoeBolts, drawGems, drawPlayerBody, drawSwipes,
  type Bounds,
} from './draw-entities';

const GRID = 80;

function hexRgb(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private viewW = 0;
  private viewH = 0;
  private gridPattern: CanvasPattern | null = null;
  private vignette: CanvasGradient | null = null;
  private hurtRing: CanvasGradient | null = null;
  private grain: CanvasPattern | null = null;
  private flashGrads = new Map<string, CanvasGradient>();
  /** 触屏设备降级：跳过纸张噪点全屏填充。 */
  private coarse = typeof matchMedia === 'function'
    && matchMedia('(pointer: coarse)').matches;

  constructor(
    private canvas: HTMLCanvasElement,
    private camera: Camera,
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas2D unavailable');
    this.ctx = ctx;
  }

  resize(w: number, h: number, dpr: number): void {
    this.viewW = w;
    this.viewH = h;
    this.canvas.width = Math.floor(w * dpr);
    this.canvas.height = Math.floor(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.gridPattern = null; // 上下文重置后 pattern 失效
    this.flashGrads.clear();
    const edge = Math.max(w, h) * 0.74;
    this.vignette = this.ringGradient(edge, '25,25,25', 0.1);
    this.hurtRing = this.ringGradient(edge, hexRgb(PALETTE.hp), 0.5);
  }

  /** 中心透明、边缘着色的径向渐变（边缘 alpha 固定，运行时用 globalAlpha 调制）。 */
  private ringGradient(edge: number, rgb: string, a: number): CanvasGradient {
    const g = this.ctx.createRadialGradient(
      this.viewW / 2, this.viewH / 2, Math.min(this.viewW, this.viewH) * 0.32,
      this.viewW / 2, this.viewH / 2, edge,
    );
    g.addColorStop(0, `rgba(${rgb},0)`);
    g.addColorStop(1, `rgba(${rgb},${a})`);
    return g;
  }

  private flashGradient(color: string): CanvasGradient {
    let g = this.flashGrads.get(color);
    if (!g) {
      g = this.ringGradient(this.viewH * 0.72, hexRgb(color), 0.55);
      this.flashGrads.set(color, g);
    }
    return g;
  }

  render(engine: Engine, alpha: number, now: number, dt: number): void {
    const ctx = this.ctx;
    const p = engine.player;
    const px = lerp(p.prevX, p.x, alpha);
    const py = lerp(p.prevY, p.y, alpha);
    this.camera.follow(px, py, this.viewW, this.viewH, dt, now);
    const camX = this.camera.x + this.camera.offsetX;
    const camY = this.camera.y + this.camera.offsetY;

    ctx.fillStyle = PALETTE.ivory;
    ctx.fillRect(0, 0, this.viewW, this.viewH);

    ctx.save();
    ctx.translate(-camX, -camY);
    const base = ctx.getTransform();
    const bounds: Bounds = {
      x0: camX - 120, y0: camY - 120,
      x1: camX + this.viewW + 120, y1: camY + this.viewH + 120,
    };
    this.drawGrid(camX, camY);
    drawGems(ctx, engine, alpha, now, bounds);
    drawCoins(ctx, engine, alpha, now, bounds);
    drawEnemies(ctx, engine, alpha, bounds, base);
    drawFoeBolts(ctx, engine, alpha, bounds);
    drawSwipes(ctx, engine, bounds);
    drawBolts(ctx, engine, alpha, bounds, base);
    drawBoomerangs(ctx, engine, alpha, base);
    this.drawParticles(engine, bounds);
    drawDamageNumbers(ctx, engine, bounds);
    drawPlayerBody(ctx, engine, px, py);
    ctx.restore();

    this.drawOverlays(engine, now);
  }

  /** 屏幕空间：常驻暗角 / 低血量红晕 / 事件闪光 / 纸张噪点。 */
  private drawOverlays(engine: Engine, now: number): void {
    const ctx = this.ctx;
    if (this.vignette) {
      ctx.fillStyle = this.vignette;
      ctx.fillRect(0, 0, this.viewW, this.viewH);
    }
    const ratio = clamp(engine.player.hp / engine.player.stats.maxHp, 0, 1);
    if (ratio < 0.3 && this.hurtRing) {
      const pulse = 0.55 + 0.45 * Math.sin(now / 180);
      ctx.globalAlpha = ((0.3 - ratio) / 0.3) * pulse;
      ctx.fillStyle = this.hurtRing;
      ctx.fillRect(0, 0, this.viewW, this.viewH);
      ctx.globalAlpha = 1;
    }
    if (this.camera.flash > 0) {
      ctx.globalAlpha = this.camera.flash;
      ctx.fillStyle = this.flashGradient(this.camera.flashColor);
      ctx.fillRect(0, 0, this.viewW, this.viewH);
      ctx.globalAlpha = 1;
    }
    if (!this.coarse) {
      if (!this.grain) this.grain = this.buildGrain();
    }
    if (!this.coarse && this.grain) {
      ctx.globalAlpha = 0.5;
      ctx.fillStyle = this.grain;
      ctx.fillRect(0, 0, this.viewW, this.viewH);
      ctx.globalAlpha = 1;
    }
  }

  /** 纸张噪点：固定种子 mulberry32 生成 128² 灰度颗粒，平铺。 */
  private buildGrain(): CanvasPattern | null {
    const S = 128;
    const c = document.createElement('canvas');
    c.width = S;
    c.height = S;
    const g = c.getContext('2d');
    if (!g) return null;
    const img = g.createImageData(S, S);
    const r = mulberry32(0x6a1ec0de);
    for (let i = 0; i < S * S; i++) {
      const v = r() < 0.5 ? 0 : 255;
      img.data[i * 4] = v;
      img.data[i * 4 + 1] = v;
      img.data[i * 4 + 2] = v;
      img.data[i * 4 + 3] = r() * 12;
    }
    g.putImageData(img, 0, 0);
    return this.ctx.createPattern(c, 'repeat');
  }

  /** 网格离屏 pattern：tile 为一格，线在右 / 下边缘。 */
  private drawGrid(camX: number, camY: number): void {
    if (!this.gridPattern) {
      const tile = document.createElement('canvas');
      tile.width = GRID;
      tile.height = GRID;
      const g = tile.getContext('2d');
      if (g) {
        g.strokeStyle = PALETTE.gridLine;
        g.lineWidth = 1;
        g.beginPath();
        g.moveTo(GRID - 0.5, 0);
        g.lineTo(GRID - 0.5, GRID);
        g.moveTo(0, GRID - 0.5);
        g.lineTo(GRID, GRID - 0.5);
        g.stroke();
      }
      this.gridPattern = this.ctx.createPattern(tile, 'repeat');
    }
    const ctx = this.ctx;
    if (this.gridPattern) {
      ctx.fillStyle = this.gridPattern;
      ctx.fillRect(camX, camY, this.viewW, this.viewH);
    }
  }

  private drawParticles(engine: Engine, b: Bounds): void {
    const ctx = this.ctx;
    for (const p of engine.particlePool.all()) {
      if (!p.active) continue;
      if (p.x < b.x0 || p.x > b.x1 || p.y < b.y0 || p.y > b.y1) continue;
      const a = particleAlpha(p);
      if (a <= 0.01) continue;
      ctx.globalAlpha = a;
      if (p.ring) {
        ctx.strokeStyle = p.color;
        ctx.lineWidth = 3 * a + 0.5;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * (0.5 + a * 0.5), 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }
}
